import React, { Component } from 'react'
import { withTranslation, WithTranslation } from 'react-i18next'

import {
  ArcElement,
  BarElement,
  CategoryScale,
  ChartData,
  Chart as ChartJS,
  ChartOptions,
  Colors,
  Legend,
  LinearScale,
  Title,
  Tooltip,
} from 'chart.js'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import { Bar, Doughnut } from 'react-chartjs-2'
import ByMakesChart from './ByMakesChart'
import ByProductChart from './ByProductChart'
import ByStatusChart from './ByStatusChart'

export interface DoughnutChart {
  data: ChartData<'doughnut', number[], string>
  options: ChartOptions<'doughnut'>
}

export interface BarChart {
  data: ChartData<'bar', number[], string>
  options: ChartOptions<'bar'>
}

type DashBoardProps = WithTranslation & {
  onChangeFilter: (days: string) => void
  onFuelTypeFilterChange: (status: string) => void
  statusInFuelType: string
  refreshVehicleChart: boolean
  refreshExpirationChart: boolean
  selectedTenant?: string
  companyId?: string
  expirationLoading: boolean
  filterWithinDays: string
  showChart: boolean
  showAdditionalCharts: boolean
  expirationYears: number[]
  chartsAnimation: boolean
  stateData: DoughnutChart
  productData: BarChart
  expirationData: BarChart
  fuelData: DoughnutChart
  vehicleData: BarChart
  isTreeView?: boolean
  color?: 'green' | 'purple'
}

class DashboardCharts extends Component<DashBoardProps> {
  constructor(props: DashBoardProps) {
    super(props)
    ChartJS.register(
      CategoryScale,
      LinearScale,
      ArcElement,
      BarElement,
      ChartDataLabels,
      Title,
      Tooltip,
      Legend,
      Colors
    )
  }
  render() {
    const { t } = this.props
    return (
      <>
        {!!this.props.stateData && (
          <div className={'col-6 col-sm-6 col-lg-6 col-md-6'}>
            <ByStatusChart
              tenantId={this.props.selectedTenant}
              companyId={this.props.companyId}
              treeview={this.props.isTreeView}
              color={this.props.isTreeView ? 'green' : 'magenta'}
            />
          </div>
        )}
        {!!this.props.productData && this.props.showAdditionalCharts && (
          <div className={'col-6 col-sm-6 col-lg-6 col-md-6'}>
            <ByProductChart
              color={this.props.isTreeView ? 'green' : 'magenta'}
              tenantId={this.props.selectedTenant}
              companyId={this.props.companyId}
              treeview={this.props.isTreeView}
            />
          </div>
        )}
        {!!this.props.fuelData && this.props.showAdditionalCharts && (
          <div className={'col-6 col-sm-6 col-lg-6 col-md-6'}>
            <div className={`box-title ${this.props.isTreeView ? 'green-title' : ''}`}>
              <span>
                <i className="icon-statistics"></i>
                {t('BY_FUEL_TYPE')}
              </span>
              <div className="selectArea">
                <select
                  onChange={(e) => this.filterFuelTypeByStatus(e)}
                  defaultValue={'ACTIVE'}
                  className="filterByExpiration"
                >
                  <option value={'ACTIVE'}>{t('ACTIVE')}</option>
                  {!!this.props.statusInFuelType &&
                    this.props.stateData?.data?.labels?.map((el, index: number) =>
                      el === t('RUNNING') || el === t('EXTENDED') ? null : (
                        <option key={index} value={el} selected={el === this.props.statusInFuelType ? true : false}>
                          {t(el)}
                        </option>
                      )
                    )}
                </select>
                <i className="icon-bottom" />
              </div>
            </div>
            <div style={{ height: '400px' }} className="box3 white">
              <Doughnut data={this.props.fuelData.data} options={this.props.fuelData.options} />
            </div>
          </div>
        )}
        {!!this.props.expirationData && (
          <div className={'col-12 col-sm-6 col-lg-6 col-md-6'}>
            <div className={`box-title ${this.props.isTreeView ? 'green-title' : ''}`}>
              <span>
                <i className="icon-statistics"></i>
                {t('BY_EXPIRATION')}
              </span>
              <div className="selectArea">
                <select
                  onChange={(e) => this.filterWithinDaysForChartChange(e)}
                  defaultValue={'All'}
                  className="filterByExpiration"
                >
                  <option selected value={'All'}>
                    {t('ALL')}
                  </option>
                  {this.props.expirationYears?.map((el, i) => (
                    <option key={i} value={el}>
                      {el}
                    </option>
                  ))}
                </select>
                <i className="icon-bottom" />
              </div>
            </div>
            <div style={{ height: '400px' }} className="box3 white">
              {this.props.expirationData ? (
                <Bar data={this.props.expirationData.data} options={this.props.expirationData.options} />
              ) : (
                !this.props.expirationLoading && (
                  <div className="noResultsCenter">
                    <h4>
                      {(this.props.filterWithinDays === '180' && t('NO_EXPIRING_CONTRACTS_WITHIN_180_DAYS')) ||
                        (this.props.filterWithinDays === '90' && t('NO_EXPIRING_CONTRACTS_WITHIN_90_DAYS')) ||
                        (this.props.filterWithinDays === '30' && t('NO_EXPIRING_CONTRACTS_WITHIN_30_DAYS'))}
                    </h4>
                  </div>
                )
              )}
            </div>
          </div>
        )}
        {!!this.props.vehicleData && !this.props.refreshVehicleChart && (
          <div
            className={
              this.props.vehicleData?.data?.labels?.length && this.props.vehicleData?.data?.labels?.length <= 10
                ? 'col-12 col-sm-6 col-lg-6 col-md-6'
                : 'col-12 col-sm-12 col-lg-12 col-md-12'
            }
          >
            {
              <ByMakesChart
                color={this.props.isTreeView ? 'green' : 'magenta'}
                tenantId={this.props.selectedTenant}
                companyId={this.props.companyId}
                treeview={this.props.isTreeView}
              />
            }
          </div>
        )}
      </>
    )
  }

  private filterWithinDaysForChartChange(e: React.ChangeEvent<HTMLSelectElement>) {
    this.props.onChangeFilter(e.target.value)
  }

  private filterFuelTypeByStatus(e: React.ChangeEvent<HTMLSelectElement>) {
    this.props.onFuelTypeFilterChange(e.target.value)
  }
}

// eslint-disable-next-line react-refresh/only-export-components
export default withTranslation('common')(DashboardCharts)
